import { ComplaintFormData, ApiResponse } from '../types';
import i18n from '../i18n';

/**
 * Map of i18n language codes to their Estonian names for API submission
 * This allows easy extension for future language support
 */
const LANGUAGE_MAP: Record<string, string> = {
  'et': 'Eesti',
  'en': 'Inglise',
};

/**
 * Recursively generates a unique filename by appending a number if the filename already exists
 * @param baseFileName - The original filename
 * @param existingFileNames - Set of filenames that have already been used
 * @param counter - Internal counter for recursion (starts at 0)
 * @returns A unique filename
 */
const generateUniqueFileName = (
  baseFileName: string,
  existingFileNames: Set<string>,
  counter: number = 0
): string => {
  // If counter is 0, try the original filename first
  if (counter === 0) {
    if (!existingFileNames.has(baseFileName)) {
      return baseFileName;
    }
    // If original exists, try with counter 1
    return generateUniqueFileName(baseFileName, existingFileNames, 1);
  }

  // Split filename into name and extension
  const lastDotIndex = baseFileName.lastIndexOf('.');
  
  let nameWithoutExt: string;
  let extension: string;
  
  if (lastDotIndex === -1) {
    // No extension found
    nameWithoutExt = baseFileName;
    extension = '';
  } else {
    nameWithoutExt = baseFileName.substring(0, lastDotIndex);
    extension = baseFileName.substring(lastDotIndex);
  }

  // Create filename with counter: name_counter.ext
  const uniqueFileName = `${nameWithoutExt}_${counter}${extension}`;

  // If this filename doesn't exist, use it
  if (!existingFileNames.has(uniqueFileName)) {
    return uniqueFileName;
  }

  // Otherwise, try with incremented counter
  return generateUniqueFileName(baseFileName, existingFileNames, counter + 1);
};

/**
 * Converts a File to base64 blob data
 * This creates a blob and then converts it to base64 for Logic Apps compatibility
 */
const fileToBlob = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    // Read the file as data URL to get base64 content
    reader.readAsDataURL(file);
    
    reader.onload = () => {
      const result = reader.result as string;
      
      if (!result) {
        reject(new Error('Failed to read file'));
        return;
      }
      
      // Extract just the base64 content (after the comma)
      const commaIndex = result.indexOf(',');
      if (commaIndex === -1) {
        reject(new Error('Invalid data URL format'));
        return;
      }
      
      const base64Content = result.substring(commaIndex + 1);
      
      resolve(base64Content);
    };
    
    reader.onerror = error => reject(error);
  });
};

/**
 * Submits contact form data to the backend API as JSON
 * Files are converted to SharePoint Logic Apps specific format with $content and $content-type
 * This format is required for SharePoint's "Add Attachment" action in Logic Apps
 */
export const submitContactForm = async (
  formData: ComplaintFormData, 
  recaptchaToken: string
): Promise<ApiResponse> => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const apiUrl = `${apiBaseUrl.replace(/\/$/, '')}`;
  
  // Validate API URL is configured
  if (!apiBaseUrl) {
    return {
      success: false,
      message: 'Süsteemi viga: API URL pole konfigureeritud. Palun võtke ühendust administraatoriga.',
      error: 'API_URL_NOT_CONFIGURED'
    };
  }

  // Try to make a real API call first, fall back to simulation
  try {
    // Convert files to SharePoint Logic Apps format
    const documents: Array<{
      fileName: string; 
      contentType: string; 
      fileSize: number; 
      fileContent: {
        "$content": string;
        "$content-type": string;
      };
      lastModified?: string;
    }> = [];
    
    if (formData.dokument && formData.dokument.length > 0) {
      // Track all filenames to ensure uniqueness
      const usedFileNames = new Set<string>();
      
      try {
        // Process files sequentially to avoid race conditions when generating unique filenames
        for (const file of Array.from(formData.dokument)) {
          try {
            // Generate a unique filename (checking against all previously processed files)
            const uniqueFileName = generateUniqueFileName(file.name, usedFileNames);
            usedFileNames.add(uniqueFileName);
            
            const blobContent = await fileToBlob(file);
            documents.push({
              fileName: uniqueFileName,
              contentType: file.type || 'application/octet-stream', // Default MIME type if not specified
              fileSize: file.size,
              // SharePoint Logic Apps specific format
              fileContent: {
                "$content": blobContent,
                "$content-type": file.type || 'application/octet-stream'
              },
              lastModified: new Date(file.lastModified).toISOString()
            });
          } catch (error) {
            console.error(`Error processing file ${file.name}:`, error);
            throw new Error(`Faili ${file.name} töötlemine ebaõnnestus. Palun proovige uuesti.`);
          }
        }
      } catch (error) {
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Failide töötlemine ebaõnnestus. Palun kontrollige faile ja proovige uuesti.',
          error: 'FILE_PROCESSING_ERROR'
        };
      }
    }

    // Get current language and convert to Estonian names using map
    const currentLanguage = i18n.language || 'et';
    const languageValue = LANGUAGE_MAP[currentLanguage] || LANGUAGE_MAP['et'];

    // Create JSON payload optimized for SharePoint list integration
    const jsonPayload = {
      // Form data
      ettevote: formData.ettevote,
      epost: formData.epost,
      probleem: formData.probleem,
      tellimus: formData.tellimus,
      
      // Language selection
      language: languageValue,
      
      // File attachments in SharePoint-compatible format
      attachments: documents,
      attachmentCount: documents.length,
      
      // Metadata
      recaptchaToken: recaptchaToken,
      submissionTimestamp: new Date().toISOString(),
      formVersion: '1.0',
      
      // Additional metadata that might be useful for SharePoint workflows
      metadata: {
        userAgent: navigator.userAgent,
        submissionId: `complaint_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        hasAttachments: documents.length > 0,
        totalAttachmentSize: documents.reduce((total, doc) => total + doc.fileSize, 0)
      }
    };

    // Set up request with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort("Took too long to submit"), 120000); // 120 second timeout

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonPayload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      return {
        success: true,
        message: "Vormi saatmine õnnestus"
      }
    } else {
      // Handle HTTP error responses
      let errorMessage = 'Vormi saatmine ebaõnnestus. Palun proovige hiljem uuesti.';
      let errorCode = `HTTP_${response.status}`;
      
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        if (errorData.error) {
          errorCode = errorData.error;
        }
      } catch {
        // If we can't parse the error response, use status-based messages
        switch (response.status) {
          case 400:
            errorMessage = 'Vigased andmed. Palun kontrollige sisestatud informatsiooni.';
            break;
          case 401:
            errorMessage = 'Autentimine ebaõnnestus. Palun laadige leht uuesti.';
            break;
          case 403:
            errorMessage = 'Juurdepääs keelatud. Palun võtke ühendust administraatoriga.';
            break;
          case 413:
            errorMessage = 'Failid on liiga suured. Maksimaalselt 10 faili, kogusuurus kuni 50MB.';
            break;
          case 429:
            errorMessage = 'Liiga palju päringuid. Palun oodake ja proovige hiljem uuesti.';
            break;
          case 500:
            errorMessage = 'Serveri viga. Palun proovige hiljem uuesti.';
            break;
          case 502:
          case 503:
          case 504:
            errorMessage = 'Teenus pole hetkel kättesaadav. Palun proovige hiljem uuesti.';
            break;
          default:
            errorMessage = `Serveri viga (${response.status}). Palun proovige hiljem uuesti.`;
        }
      }

      return {
        success: false,
        message: errorMessage,
        error: errorCode
      };
    }
  } catch (error) {
    console.error('Form submission error:', error);
    
    // Handle specific error types
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          success: false,
          message: 'Vormi saatmine võttis liiga kaua aega. Palun kontrollige internetiühendust ja proovige uuesti.',
          error: 'TIMEOUT_ERROR'
        };
      }
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return {
          success: false,
          message: 'Internetiühenduse viga. Palun kontrollige ühendust ja proovige uuesti.',
          error: 'NETWORK_ERROR'
        };
      }

      if (error.message.includes('faili') || error.message.includes('File')) {
        return {
          success: false,
          message: error.message,
          error: 'FILE_PROCESSING_ERROR'
        };
      }
    }

    // Generic error fallback
    return {
      success: false,
      message: 'Ootamatu viga vormi saatmisel. Palun proovige hiljem uuesti.',
      error: 'UNKNOWN_ERROR'
    };
  }
};
